import type {ReactNode} from 'react';

// In-house replacement for react-toastify's `toast` API (only the parts the site uses).
// Toasts live in this module-level store, so one raised before <ToastContainer> has mounted
// is kept and shown as soon as it mounts — the same queueing react-toastify did.

export type ToastType = 'success' | 'error';

export interface ToastItem {
    id: number;
    type: ToastType;
    message: ReactNode;
    /** Set when the toast should play its exit animation. */
    closing: boolean;
}

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());

const add = (type: ToastType, message: ReactNode): number => {
    const id = nextId++;
    // Appended at the end: newest toast at the bottom (react-toastify `newestOnTop={false}`).
    items = [...items, {id, type, message, closing: false}];
    emit();
    return id;
};

export const toast = {
    success: (message: ReactNode) => add('success', message),
    error: (message: ReactNode) => add('error', message),
    /** Starts the exit animation; the container removes it once the animation ends. */
    dismiss: (id: number) => {
        items = items.map((item) => (item.id === id ? {...item, closing: true} : item));
        emit();
    }
};

/** Drops a toast from the store after its exit animation. Used by ToastContainer. */
export const removeToast = (id: number) => {
    items = items.filter((item) => item.id !== id);
    emit();
};

export const subscribeToasts = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const getToasts = () => items;

const EMPTY: ToastItem[] = [];
export const getServerToasts = () => EMPTY;
